import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, EyeIcon, FlagIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';
import { TanTable, type BulkEditMeta } from '../TanTable';
import type { BulkAction } from '../TableActionBar';
import { Tooltip } from '../ui/Tooltip';
import { config } from '../../config/config';
import Notification, { NotificationContainer } from '../Notification';
import { deletedRowClass } from '../../utils/deletedRow';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import type { DataTableDef, TablemaintField } from '../../config/dataTables.registry';
import { BoolPill, normalizeOptions } from './fields';
import { RosterCell } from './RosterCell';
import { StackedCell } from './StackedCell';
import TablemaintDrawer, { type SaveNotification } from './TablemaintDrawer';
import { DRAWER_PANELS } from './drawerPanels';
import { useFieldOptions } from './useFieldOptions';
import { useGridColumnPicker } from './useGridColumnPicker';
import { bulkEditMetaFor, massEditSummary } from './massEdit';
import { EditableCell } from '../gridEdit/EditableCell';
import { useStagedEdits } from '../gridEdit/useStagedEdits';
import { retriggerEdiEventsBulk, type EdiRetriggerSource } from '../../services/edievents.service';
import { titleCase } from '../../utils/titleCase';
import { keyAccountClass } from '../../pages/order-management/quoteColors';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';

/** A table's bespoke edit drawer (e.g. Bill Tos, Customers). Renders in place of
 *  the generic field-spec drawer; manages its own fields/sections/save.
 *  `row` is null when creating a new record (tables that allow create). */
export interface CustomDrawerProps {
  row: TablemaintRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * The standardized Data Management table-maintenance page.
 *
 * Extracted from the hand-built Dispatchers page so every engine-driven table
 * shares one UX: TanTable with an Add (+) button and a recycle-bin "show
 * deleted" toggle (deleted rows highlighted red); clicking anywhere in a row
 * opens a right Drawer with required-asterisks, red-outline invalid fields, a
 * top error banner with auto-scroll, and the soft-delete checkbox.
 *
 * Everything is driven by `cfg.fields`; complex tables can keep their own
 * standalone page (registered as `custom`) instead of using this.
 */

// Lazy-loaded so the heavy pro-detail bundle only loads when a proLink cell opens a
// pro (keeps it out of the shared tablemaint bundle + the test import graph).
const ProDetailsView = lazy(() => import('../ProDetailsView').then((m) => ({ default: m.ProDetailsView })));

// Lazy-loaded so the Google-Maps dependency stays out of the shared tablemaint bundle
// (and test import graph) until a user opens the ZIP map from a `mapZip` field.
const ZipLocationMapModal = lazy(() => import('../ZipLocationMapModal'));

const isTruthy = (v: any): boolean => v === 'Y' || v === true || v === 1;

/** Display form of an idKey FK recno: '' for unset (null/blank/0 — 0 means "any"
 *  on optional FK columns), else the id as a string. */
const fkIdDisplay = (raw: any): string => {
  if (raw == null || raw === '' || Number(raw) === 0) return '';
  return String(raw);
};

/** A `multiValue` cell's entries. The server joins them with ', ' (GROUP_CONCAT),
 *  and the grid needs them one at a time so a column filter, its facets and a pasted
 *  Match List each match a single entry rather than the whole joined string. Blanks
 *  are dropped so an empty cell faceted as "(Blank)" rather than an empty token. */
const splitMultiValue = (raw: any): string[] =>
  String(raw ?? '').split(',').map((v) => v.trim()).filter((v) => v !== '');

// Default row cap for server-searched tables (too large to load fully client-side).
const DEFAULT_SERVER_SEARCH_LIMIT = 500;

/** The action bar's buttons carry a colour vocabulary, one hue per kind of action:
 *  blue = Change Template for All, amber = Change All, teal = Trace All, violet =
 *  Documents, orange/indigo = the LC mass sends, red = destructive (Clear All Leg
 *  Appt Dates). Emerald is the free slot and reads as "send", which is what a
 *  retrigger is: the record goes out again on the next EDI run. Same shape as the
 *  rest so the bar reads as one set rather than a stray button. */
const BULK_RETRIGGER_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white ' +
  'bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed';

interface Props {
  cfg: DataTableDef;
  /** Optional bespoke edit drawer (e.g. Bill Tos). When set, it replaces the
   *  generic field-spec drawer; the page still renders the table + chrome. */
  DrawerComponent?: React.ComponentType<CustomDrawerProps>;
}

const alignClass = (a?: 'left' | 'center' | 'right'): string =>
  a === 'right' ? 'block text-right' : a === 'center' ? 'block text-center' : '';

/** Pill classes for a `badgeColors` status cell, by tone. */
const BADGE_TONES: Record<string, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300',
};

/** Render a stored date ('YYYY-MM-DD', or the '0000-00-00'/empty sentinel) as
 *  'MM/DD/YYYY' (blank when unset) for a 'date' table column. */
const fmtDate = (v: any): string => {
  const s = v == null ? '' : String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m || m[1] === '0000') return '';
  return `${m[2]}/${m[3]}/${m[1]}`;
};

/** Parse a date/datetime-looking cell value to an epoch (ms), or null if it isn't one.
 *  Handles the backend's display format 'MM/DD/YYYY[ HH:mm]' and ISO 'YYYY-MM-DD[ HH:mm]'
 *  (the '0000-…' zero-date sentinel → null). */
const parseDateish = (v: any): number | null => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?/.exec(s);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2], +(m[4] ?? 0), +(m[5] ?? 0)).getTime();
  m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(s);
  if (m) return m[1] === '0000' ? null : new Date(+m[1], +m[2] - 1, +m[3], +(m[4] ?? 0), +(m[5] ?? 0)).getTime();
  return null;
};

/** Date-aware column sort: when both cells parse as dates (e.g. the 'MM/DD/YYYY HH:mm'
 *  datetime-display columns) they compare chronologically instead of alphanumerically;
 *  otherwise it falls back to a numeric-aware string compare (so numbers + plain text still
 *  order correctly). Applied to every non-multiselect tablemaint column. */
const dateAwareSort = (rowA: any, rowB: any, columnId: string): number => {
  const av = rowA.getValue(columnId);
  const bv = rowB.getValue(columnId);
  const a = parseDateish(av);
  const b = parseDateish(bv);
  if (a !== null && b !== null) return a - b;
  if (a !== null) return -1; // real dates sort before blanks/non-dates
  if (b !== null) return 1;
  return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

/** Toolbar glyphs for cfg.nonBlankFilter. Deliberately NOT a funnel: that is
 *  already TanTable's column-filter icon, so reusing it here would say "a filter"
 *  twice and "which rows" never. Like the trash (deleted) and the archive box
 *  (archived), each of these names the rows the toggle is about. */
const ROW_FILTER_ICONS = {
  alert: ExclamationTriangleIcon,
  flag: FlagIcon,
} as const;

const TablemaintPage: React.FC<Props> = ({ cfg, DrawerComponent }) => {
  // Deep-link support: a `?q=` query param pre-seeds the table search, so other
  // pages can link straight to a specific record (e.g. a quote's Cust Tariff #
  // → this page pre-filtered to that tariff number).
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('q') ?? '';
  // Column-scoped deep-link filters (cfg.filterParams): URL params forwarded to the list
  // fetch as server-side filters (e.g. Report Monitoring History pre-filtered to a report's
  // system + name). Empty object when the table declares none.
  const deepLinkFilters = useMemo(() => {
    const out: Record<string, string> = {};
    (cfg.filterParams ?? []).forEach((p) => {
      const v = searchParams.get(p);
      if (v) out[p] = v;
    });
    return out;
  }, [cfg.filterParams, searchParams]);
  const deepLinkFiltersKey = JSON.stringify(deepLinkFilters);

  // Human-readable chips for the active deep-link filters + a one-click "View
  // All" that wipes the filter conditions from the URL (so a non-technical user
  // can see what's filtered and get back to the full list without editing the
  // URL). Recomputing deepLinkFilters → re-fetches unfiltered.
  const deepLinkFilterChips = useMemo(
    () =>
      Object.entries(deepLinkFilters).map(([key, val]) => {
        const field = (cfg.fields ?? []).find((f) => f.key === key);
        return { key, label: field?.label ?? titleCase(key), value: val };
      }),
    [deepLinkFilters, cfg.fields],
  );
  const clearDeepLinkFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    (cfg.filterParams ?? []).forEach((p) => next.delete(p));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, cfg.filterParams]);

  const idKey = cfg.idKey ?? 'recno';
  const softDeleteCol = cfg.softDelete ?? 'deleted';
  const softDeleteInvert = !!cfg.softDeleteInvert; // column is an ACTIVE flag (Y=visible)
  const hasSoftDelete = !cfg.noSoftDelete;
  const singular = cfg.singular ?? cfg.label;
  const fields = cfg.fields ?? [];
  // Server-search mode: huge tables (e.g. EDI Events) load only a capped page and
  // push the search box term to the server rather than holding every row in memory.
  const serverSearch = !!cfg.serverSearch;
  const searchLimit = cfg.serverSearchLimit ?? DEFAULT_SERVER_SEARCH_LIMIT;

  const isDeleted = useCallback(
    // Inverted columns store an ACTIVE flag: 'Y' = visible, anything else = deleted.
    (row: TablemaintRow) => (softDeleteInvert ? !isTruthy(row[softDeleteCol]) : isTruthy(row[softDeleteCol])),
    [softDeleteCol, softDeleteInvert],
  );

  const [data, setData] = useState<TablemaintRow[]>([]);
  const [dateSelection, setDateSelection] = useState('latest');
  const dateValues = useMemo(() => cfg.latestDateFilter
    ? [...new Set(data.map(row => String(row[cfg.latestDateFilter!.column] || '').slice(0, 10)).filter(Boolean))].sort().reverse()
    : [], [data, cfg.latestDateFilter]);
  const activeDate = dateSelection === 'latest' ? dateValues[0] : dateSelection;
  const visibleData = useMemo(() => cfg.latestDateFilter && activeDate
    ? data.filter(row => String(row[cfg.latestDateFilter!.column] || '').slice(0, 10) === activeDate)
    : data, [data, cfg.latestDateFilter, activeDate]);
  useEffect(() => { setDateSelection('latest'); }, [cfg.key]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  // Default-on non-blank filter (cfg.nonBlankFilter), e.g. Melrose Orders opening on
  // just the failed imports. Starts APPLIED, so the state it tracks is the relaxation.
  const [includeBlank, setIncludeBlank] = useState(false);
  // Debounced server-search term (server-search tables only).
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  // Target of TanTable's "Match List" filter while it is being resolved SERVER-side
  // (capped tables only — see cfg.listFilterColumns). A ref because the handler has to
  // AWAIT the refetch it triggers: TanTable uses the resolved promise to know when the
  // rows are authoritative enough to reconcile "43 of 50 matched" against.
  const listFilterRef = useRef<{ column: string; values: string[] } | null>(null);
  // Mirror of the above for rendering, since a ref does not re-render.
  const [listFilterActive, setListFilterActive] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dynamic select options fetched from each field's `optionsEndpoint`. Needed here
  // for the multiselect grid cells' value->label maps, and handed to the drawer so it
  // doesn't fetch the same endpoints a second time.
  const dynamicOptions = useFieldOptions(fields);
  // A row-backed choice list updates as soon as a new value is saved and the
  // table reloads. Category on Override Reasons uses this for its combobox.
  const rowOptions = useMemo(() => {
    const options: Record<string, string[]> = {};
    fields.filter((f) => f.optionsFromRows).forEach((f) => {
      options[f.key] = [...new Set(data.map((row) => String(row[f.key] ?? '').trim()).filter(Boolean))].sort();
    });
    return options;
  }, [fields, data]);
  const availableOptions = useMemo(() => ({ ...dynamicOptions, ...rowOptions }), [dynamicOptions, rowOptions]);
  // Dynamic columns: any other whitelisted column, addable through TanTable's
  // column-insert picker (Column Edit Mode → +). Owns its own persistence.
  const picker = useGridColumnPicker(cfg.key, fields, hasSoftDelete ? [softDeleteCol] : []);
  const { extraFields, requestFields, ready: pickerReady, columnMeta, dbNameFor } = picker;
  // Grid edits are held until Save — see useStagedEdits. Nothing a drag or a
  // keystroke does reaches the server on its own, so a mis-drag is just Discard.
  const staged = useStagedEdits();

  // ── Inline editing is OFF until asked for, per column ───────────────────────
  // These grids are read far more often than they are edited, and an input under
  // every cursor gets in the way of the reading: a dropdown swallows the click that
  // was trying to select the text, and copying a column out means fighting a widget
  // in every cell. Users asked for a switch, and TanTable already had the contract
  // for one (canEditColumn / isColumnEditing / onToggleColumnEditing) — built for
  // Search Results and never adopted here, which is why these tables were always on.
  //
  // Per column rather than per table: it matches what TanTable already models, it
  // puts the switch on the column the user is looking at, and arming one column is a
  // smaller blast radius than arming all of them.
  const [editingCols, setEditingCols] = useState<Set<string>>(() => new Set());
  const [retriggering, setRetriggering] = useState(false);

  // Nothing stays staged behind a switch that says editing is off. Turning a column
  // off discards what IT staged and leaves every other column's edits alone.
  const toggleColumnEditing = useCallback((colId: string) => {
    setEditingCols((prev) => {
      const next = new Set(prev);
      if (next.delete(colId)) staged.discardCells((_id, column) => column === colId);
      else next.add(colId);
      return next;
    });
  }, [staged]);

  // Leaving the page's edit session entirely (Save, Discard, a reload) also drops the
  // arming, so a grid never comes back with a column silently live.
  const disarmAllColumns = useCallback(() => setEditingCols(new Set()), []);
  const [saving, setSaving] = useState(false);
  const requestFieldsKey = requestFields.join(',');

  // Pro opened from a `proLink` cell (ProDetailsView drawer — same as the dashboards).
  const [selectedPro, setSelectedPro] = useState<string | null>(null);

  const [editing, setEditing] = useState<TablemaintRow | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);

  // Debounced search box → server refetch (server-search tables). Client-side
  // filtering still runs on the returned page, which is a no-op refinement.
  const onServerSearch = useCallback((term: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchTerm(term), 350);
  }, []);

  const loadData = useCallback(async () => {
    // Wait for the saved dynamic-column selection: fetching first would run the
    // whole list query twice, once without the user's extra columns.
    if (!pickerReady) return;
    setLoading(true);
    setLoadError(null);
    const listFilter = listFilterRef.current;
    try {
      // A pasted list asks the TABLE, uncapped, so the grid's reconciliation is true.
      // It also drops the search term: the two would fight over the same request, and
      // TanTable's own search still refines the returned rows client-side — the same
      // no-op-refinement arrangement the capped default load already relies on.
      setData(listFilter
        ? await tablemaintService.listByValues(cfg.key, listFilter.column, listFilter.values, {
            includeDeleted,
            includeArchived,
            ...(requestFields.length ? { fields: requestFields } : {}),
          })
        : await tablemaintService.list(cfg.key, {
            includeDeleted,
            includeArchived,
            ...(cfg.nonBlankFilter ? { includeBlank } : {}),
            ...(requestFields.length ? { fields: requestFields } : {}),
            ...(serverSearch ? { q: searchTerm, pageSize: searchLimit } : {}),
            ...(Object.keys(deepLinkFilters).length ? { filters: deepLinkFilters } : {}),
          }));
    } catch (err: any) {
      console.error(`Error loading ${cfg.key}:`, err);
      setLoadError(err?.response?.data?.detail || `Could not load ${cfg.label} from the server.`);
      // A list the server refused (too many values, say) must not poison every later
      // load — drop it so Retry, a toggle, or a save comes back cleanly.
      if (listFilter) {
        listFilterRef.current = null;
        setListFilterActive(false);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.key, cfg.label, includeDeleted, includeArchived, includeBlank, serverSearch, searchTerm, searchLimit, deepLinkFiltersKey, pickerReady, requestFieldsKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Resolves once the rows for this list have landed, which is TanTable's cue that the
  // reconciliation it shows is answering against the table rather than the loaded page.
  const handleListFilterRequest = useCallback(async (columnId: string, values: string[]) => {
    listFilterRef.current = values.length ? { column: columnId, values } : null;
    setListFilterActive(values.length > 0);
    await loadData();
  }, [loadData]);

  const handleEditOpen = useCallback((row: TablemaintRow) => {
    setEditing(row);
    setIsAdding(false);
  }, []);

  // Can a row be opened at all? A readonly table normally has nothing to open — but a
  // bespoke drawer on one is a DETAIL view (e.g. EDI 997 acknowledgments, whose useful
  // fields don't fit a grid cell), so it still opens on click. Nothing about readonly
  // changes: there is no Add, no in-place editing, and the generic save drawer stays
  // suppressed; only the affordance becomes View rather than Edit.
  const canOpen = !cfg.readonly || !!DrawerComponent;

  // Deep-link support: `?edit=<recno>` opens that record's drawer straight away
  // (the order screen's Consignee/Customer/Broker/Bill To pencil buttons link
  // here). The bespoke drawers only read `row[idKey]` and then refetch through
  // tablemaintService.get, so a synthetic row is enough — no need to wait for
  // the grid, and the record doesn't have to be on the loaded page. Latched to
  // fire once so closing the drawer doesn't immediately reopen it.
  const editParamConsumed = useRef(false);
  useEffect(() => {
    if (editParamConsumed.current || cfg.readonly) return;
    const recno = Number(searchParams.get('edit')) || 0;
    if (!recno) return;
    editParamConsumed.current = true;
    setEditing({ [idKey]: recno });
    setIsAdding(false);
  }, [searchParams, cfg.readonly, idKey]);

  const handleAddOpen = useCallback(() => {
    setEditing(null);
    setIsAdding(true);
  }, []);

  const handleClose = useCallback(() => {
    setEditing(null);
    setIsAdding(false);
  }, []);

  // Drawer saved: show its toast, then refetch. Awaited by the drawer so it stays
  // in its saving state until the list has reloaded.
  const handleSaved = useCallback(async (notification?: SaveNotification) => {
    if (notification) setSuccessNotification(notification);
    await loadData();
  }, [loadData]);

  // value->label maps for multiselect table cells (static or endpoint options).
  const msLabelMaps = useMemo(() => {
    const maps: Record<string, Map<string, string>> = {};
    fields.filter((f) => f.type === 'multiselect').forEach((f) => {
      const opts = normalizeOptions(f.optionsEndpoint ? (dynamicOptions[f.optionsEndpoint] ?? []) : f.options);
      maps[f.key] = new Map(opts.map((o) => [o.value, o.label]));
    });
    return maps;
  }, [fields, dynamicOptions]);

  // Which columns offer drag-select mass edit, and with what widget. The backend's
  // `mass_edit` role is authoritative; bulkEditMetaFor narrows it with what only the
  // registry knows (FK pickers). A table can opt out wholesale via `noMassEdit`.
  const bulkMeta = useMemo<Record<string, BulkEditMeta>>(() => {
    if (cfg.readonly || cfg.noMassEdit) return {};
    const out: Record<string, BulkEditMeta> = {};
    [...fields.filter((f) => f.inTable !== false), ...extraFields].forEach((f) => {
      // dynamicOptions is already fetched for the drawer's selects — an
      // endpoint-backed column (scac → /cms-lookups/scacs) becomes a real dropdown
      // here at no extra cost.
      const m = bulkEditMetaFor(f, columnMeta.get(f.key), availableOptions, !!DrawerComponent);
      if (m) out[f.key] = m;
    });
    return out;
  }, [fields, extraFields, columnMeta, availableOptions, DrawerComponent, cfg.readonly, cfg.noMassEdit]);

  // Drag-select finished: STAGE the range, don't write it. Nothing leaves the browser
  // until Save, so a mis-drag costs a click of Discard rather than a repair job.
  const handleBulkEdit = useCallback((columnId: string, rows: TablemaintRow[], value: unknown) => {
    const column = dbNameFor(columnId);
    // Grouped tables put group rows in the dragged range; they have no id of their own.
    // Each entry carries its stored value so a drag over rows that already hold the
    // value stages nothing for them — the count reflects what would be written.
    const entries = rows
      .filter((r) => r?.[idKey] != null && r?.[idKey] !== '')
      .map((r) => ({ recno: r[idKey], original: r[column] }));
    if (entries.length) staged.stageMany(entries, column, value);
  }, [idKey, dbNameFor, staged]);

  // Save everything staged in ONE request, however many columns, records and distinct
  // values it spans. The backend applies each record through the same validation and
  // history logging as a drawer save and names the ones that failed — those stay
  // staged so they can be fixed and retried instead of being lost with the rest.
  const handleSaveEdits = useCallback(async () => {
    if (!staged.changes.length || saving) return;
    setSaving(true);
    try {
      const res = await tablemaintService.bulkUpdate(cfg.key, staged.changes);
      const summary = massEditSummary(res.updated.length, res.unchanged.length, res.failed.length, 'record');
      if (res.failed.length) {
        const shown = res.failed.slice(0, 3).map((f) => `#${f.recno}: ${f.error}`).join(' · ');
        setErrorNotification({
          title: `${singular}: ${summary}`,
          message: `${shown}${res.failed.length > 3 ? ` · and ${res.failed.length - 3} more` : ''}`,
        });
        staged.keepOnly(res.failed.map((f) => f.recno));
      } else {
        setSuccessNotification({ title: `${singular} updated`, message: summary });
        staged.discard();
        disarmAllColumns();
      }
      await loadData();
    } catch (err: any) {
      setErrorNotification({
        title: 'Save failed',
        message: err?.response?.data?.detail || `Could not save the ${cfg.label.toLowerCase()} changes.`,
      });
    } finally {
      setSaving(false);
    }
  }, [cfg.key, cfg.label, singular, saving, staged, loadData, disarmAllColumns]);

  // ── "Do this to everything in view" ─────────────────────────────────────────
  // Drag-select stages a range, but a range you have to drag is the wrong tool for
  // 180 records: the drag only extends as far as the rendered rows, so it turns into
  // 20-at-a-time plus scrolling. This acts on the table's FILTERED rows instead —
  // the user already expressed the selection by narrowing the grid (the pasted-list
  // filter takes a whole column of invoice numbers at once), so the work is done.
  //
  // Unlike a staged grid edit this writes immediately, because there is nothing to
  // review: one flag, one meaning, already named on the button and in the confirm.
  const bulkRetriggerAction = useMemo<BulkAction[]>(() => {
    const spec = cfg.bulkRetrigger;
    // One of the two must say where the rows go. Neither means a misconfigured
    // registry entry, and showing a button that sends `undefined` as the source
    // would turn that into a server error the user gets blamed for.
    if (!spec || (!spec.source && !spec.sourceBy)) return [];
    return [{
      key: 'bulk-retrigger',
      label: spec.label,
      icon: <Send className="h-4 w-4" />,
      className: BULK_RETRIGGER_CLS,
      // One click per run. The confirm makes a double-click easy — the second one
      // lands on a dialog that is no longer there — and a second batch would be
      // invisible in the result of the first.
      disabled: retriggering,
      onRun: async (rows: TablemaintRow[]) => {
        if (retriggering) return;
        const by = spec.sourceBy;
        const recnoOf = (r: TablemaintRow) => Number(r[idKey]);
        const usable = (n: number) => Number.isFinite(n) && n > 0;

        // One batch per server source. A single-source grid is the same shape with one
        // group, so there is only one path through the rest of this.
        const groups = new Map<string, { noun: string; recnos: number[] }>();
        let skipped = 0;
        for (const row of rows) {
          const entry = by
            ? by.map[String(row[by.column] ?? '')]
            : { source: spec.source!, noun: 'record' };
          const recno = recnoOf(row);
          if (!entry || !usable(recno)) { skipped += 1; continue; }
          const g = groups.get(entry.source) ?? { noun: entry.noun, recnos: [] };
          g.recnos.push(recno);
          groups.set(entry.source, g);
        }

        const batches = [...groups.entries()];
        const total = batches.reduce((n, [, g]) => n + g.recnos.length, 0);
        if (!total) {
          setErrorNotification({
            title: 'Nothing to retrigger',
            message: skipped
              ? `None of the ${skipped} row(s) in view can be retriggered.`
              : 'There are no rows in view.',
          });
          return;
        }
        // Name each group in the PROMPT. These are different writes — clearing a sent
        // date vs lifting a row out of the archive — so "142 records" would be hiding
        // the half of the action the user is least expecting.
        const breakdown = batches.map(([, g]) => `${g.recnos.length} ${g.noun}(s)`).join('\n');
        const note = skipped ? `\n\n${skipped} row(s) in view will be skipped.` : '';
        if (!window.confirm(`${spec.confirm}\n\n${breakdown}${note}`)) return;

        setRetriggering(true);
        // Sequential, and the results accumulate as they land: each batch is its own
        // transaction, so if the second fails the first has already been written and
        // saying otherwise would send someone looking for work that did happen.
        let requested = 0;
        let retriggered = 0;
        try {
          for (const [source, g] of batches) {
            const res = await retriggerEdiEventsBulk(source as EdiRetriggerSource, g.recnos);
            requested += res.requested;
            retriggered += res.retriggered;
          }
          setSuccessNotification({
            title: spec.label,
            // "requested" can exceed "retriggered" when a record was removed between
            // the grid loading and the click; say so rather than claim a clean run.
            message: retriggered === requested
              ? `${retriggered} record(s) updated`
              : `${retriggered} of ${requested} record(s) updated`,
          });
        } catch (err: any) {
          setErrorNotification({
            title: `${spec.label} failed`,
            message: [
              err?.response?.data?.detail || 'Could not retrigger the selected records.',
              retriggered ? `${retriggered} record(s) were already updated before this failed.` : '',
            ].filter(Boolean).join(' '),
          });
        } finally {
          // Refetch either way — a failed second batch still leaves the first one's
          // writes on screen to account for.
          await loadData();
          setRetriggering(false);
        }
      },
    }];
  }, [cfg.bulkRetrigger, idKey, loadData, retriggering]);

  const columns = useMemo<ColumnDef<TablemaintRow>[]>(() => {
    const cols: ColumnDef<TablemaintRow>[] = [];
    // The registry's own columns, then the ones this user added through the field
    // picker — run through the SAME builder so an added column formats, filters and
    // sorts exactly like a declared one.
    [...fields.filter((f) => f.inTable !== false), ...extraFields]
      .forEach((f) => {
        const colId = f.colId ?? f.key;
        // `bulkMeta` says the column COULD be edited; `editingCols` says the user
        // switched it on. Both the in-place input and the drag-select range key off
        // the armed set, so an un-armed column is inert to every editing gesture
        // rather than read-only-looking but still draggable.
        const canEdit = bulkMeta[f.key];
        const editMeta = editingCols.has(colId) ? canEdit : undefined;
        const isBool = f.type === 'yn' || f.type === 'toggle';
        const isDate = f.type === 'date';
        const isMulti = f.type === 'multiselect';
        // A comma-separated LIST in one cell (EDI Invoices' Pro Number(s)). The
        // accessor hands the grid the split array so filters/facets/Match List match
        // each entry; the cell below renders the joined string unchanged.
        const isMultiValue = !!f.multiValue;
        const needsCell = isBool || isDate || isMulti || isMultiValue || f.stackedLines || f.titleCase || f.keyAccount || f.redFlag || f.badgeColors || f.proLink || !!f.idKey || !!f.deletedKey || !!f.approvedKey || !!f.inactiveKey || !!f.align;
        cols.push({
          // idKey columns fold the FK recno into the accessor value ("NAME #923")
          // so the column filter facets and the global search match the recno too;
          // the cell renders name + muted #id from row.original, so display is
          // unaffected. The column id is `colId` — f.key for every declared column,
          // and only a picker-added one can differ (see TablemaintField.colId).
          ...(f.idKey
            ? {
                id: colId,
                accessorFn: (row: TablemaintRow) => {
                  const id = fkIdDisplay(row[f.idKey!]);
                  const v = String(row[f.key] ?? '');
                  return id ? `${v} #${id}`.trim() : v;
                },
              }
            : isMultiValue
              ? { id: colId, accessorFn: (row: TablemaintRow) => splitMultiValue(row[f.key]) }
              : colId === f.key
                ? { accessorKey: f.key }
                : { id: colId, accessorFn: (row: TablemaintRow) => row[f.key] }),
          header: f.columnLabel ?? f.label,
          enableSorting: true,
          // Date-aware sort so datetime-display columns ('MM/DD/YYYY HH:mm') order
          // chronologically, not alphanumerically. (Multiselect keeps the default.)
          ...(isMulti || isMultiValue ? {} : { sortingFn: dateAwareSort }),
          enableColumnFilter: true,
          filterFn: 'arrIncludes',
          size: f.width ?? 160,
          // Stacked columns render multiple lines — opt out of TanTable's single-line
          // truncation so the whole stack shows (and the row grows to fit).
          // `bulkEdit` opts the column into drag-select mass edit; null = no affordance.
          ...(f.stackedLines || editMeta
            ? { meta: { ...(f.stackedLines ? { multiline: true } : {}), ...(editMeta ? { bulkEdit: editMeta } : {}) } }
            : {}),
          ...(editMeta
            ? {
                // Editable in place — the same set the drag-select offers, so a
                // column either edits both ways or neither.
                cell: ({ row }) => {
                  const id = row.original[idKey];
                  return (
                    <EditableCell
                      value={staged.effective(id, f.key, row.original[f.key])}
                      meta={editMeta}
                      edited={staged.isEdited(id, f.key)}
                      disabled={saving || id == null || id === ''}
                      onCommit={(next) => staged.stage(id, f.key, next, row.original[f.key])}
                    />
                  );
                },
              }
            : needsCell
            ? {
                cell: ({ row }) => {
                  const v = row.original[f.key];
                  const inner = isBool ? (
                    <BoolPill value={isTruthy(v)} />
                  ) : isDate ? (
                    fmtDate(v)
                  ) : isMultiValue ? (
                    String(v ?? '')
                  ) : isMulti ? (
                    (() => {
                      const arr = Array.isArray(v) ? (v as string[]) : [];
                      const lm = msLabelMaps[f.key];
                      const names = arr.map((x) => lm?.get(x) ?? x);
                      return (
                        <span title={names.join(', ')} className="inline-flex items-center gap-2">
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">{arr.length}</span>
                          <span className="truncate text-gray-600 dark:text-gray-300">
                            {names.slice(0, 2).join(', ')}{arr.length > 2 ? ` +${arr.length - 2}` : ''}
                          </span>
                        </span>
                      );
                    })()
                  ) : f.stackedLines ? (
                    <StackedCell value={String(v ?? '')} titleCase={f.titleCase} />
                  ) : f.inactiveKey ? (
                    <RosterCell
                      active={String(v ?? '')}
                      inactive={String(row.original[f.inactiveKey] ?? '')}
                    />
                  ) : f.keyAccount ? (
                    v ? <span className={keyAccountClass(String(v))}>{String(v)}</span> : ''
                  ) : f.redFlag ? (
                    v ? <span className="font-semibold text-red-600 dark:text-red-400">{String(v)}</span> : ''
                  ) : f.badgeColors ? (
                    v ? (
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${BADGE_TONES[f.badgeColors[String(v)] ?? 'gray']}`}>
                        {String(v)}
                      </span>
                    ) : ''
                  ) : f.proLink ? (
                    (() => {
                      const pn = String(v ?? '');
                      if (!pn) return '';
                      return (
                        <span className="inline-flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedPro(pn); }}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
                          >
                            {pn}
                          </button>
                          <a
                            href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pn}&searchtype=pronumber`}
                            target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Open in legacy CMS"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </a>
                        </span>
                      );
                    })()
                  ) : f.titleCase ? (
                    titleCase(String(v ?? ''))
                  ) : (
                    (v ?? '')
                  );
                  // idKey: trail the display value with its FK recno, muted.
                  const idVal = f.idKey ? fkIdDisplay(row.original[f.idKey]) : '';
                  const withId = idVal ? (
                    <span className="inline-flex items-baseline gap-1.5 min-w-0">
                      <span className="truncate">{inner}</span>
                      <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">#{idVal}</span>
                    </span>
                  ) : inner;
                  // deletedKey: the referenced entity is soft-deleted — flag it.
                  // Blank cells stay blank: with neither a name nor an id there
                  // is nothing to mark (e.g. an unset FK matching cms's junk
                  // recno=0 rows, which are blank-named and deleted='Y').
                  const refDeleted = f.deletedKey
                    ? isTruthy(row.original[f.deletedKey]) && (idVal !== '' || String(v ?? '').trim() !== '')
                    : false;
                  const refUnapproved = f.approvedKey
                    ? row.original[f.approvedKey] === 'N' && String(v ?? '').trim() !== ''
                    : false;
                  const flagged = refDeleted || refUnapproved ? (
                    <span className="text-red-600 dark:text-red-400">
                      {withId}
                      {refDeleted && <span className="ml-1.5 text-[0.625rem] font-semibold">(deleted)</span>}
                      {refUnapproved && <span className="ml-1.5 text-[0.625rem] font-semibold">(not approved)</span>}
                    </span>
                  ) : withId;
                  return f.align ? <span className={alignClass(f.align)}>{flagged}</span> : flagged;
                },
              }
            : {}),
        });
      });
    if (canOpen) {
      // Open-the-record affordance. Clicking the row still works, but most cells are
      // editable in place now, so a click that lands on one edits rather than opens —
      // leaving no visible way to reach the full record. Pinned right so it stays put
      // on a table wide enough to scroll. On a readonly table it opens a detail view,
      // so it reads View rather than Edit.
      const OpenIcon = cfg.readonly ? EyeIcon : PencilSquareIcon;
      const openVerb = cfg.readonly ? 'View' : 'Edit';
      cols.push({
        id: 'actions',
        header: openVerb,
        // Exactly as wide as the pencil and its label. Without fixedWidth the column
        // would take an equal share of any leftover width like every other one, which
        // on a narrow table leaves an icon marooned in a wide empty cell.
        size: 52,
        meta: { fixedWidth: true },
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-center">
            {/* "Open" reads right for both — the header and the icon carry the
                edit-vs-view distinction. */}
            <Tooltip content={`Open ${singular}`}>
              <button
                type="button"
                aria-label={`Open ${singular}`}
                onClick={(e) => { e.stopPropagation(); handleEditOpen(row.original); }}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              >
                <OpenIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        ),
      });
      if (!cfg.readonly && hasSoftDelete) {
        cols.push({
          accessorKey: softDeleteCol,
          // Inverted soft-delete reads as an "Active" column (green when visible).
          header: softDeleteInvert ? 'Active' : 'Deleted',
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: 'arrIncludes',
          size: 90,
          cell: ({ row }) =>
            softDeleteInvert ? (
              <BoolPill value={!isDeleted(row.original)} />
            ) : (
              <BoolPill value={isDeleted(row.original)} />
            ),
        });
      }
    }
    return cols;
  }, [fields, extraFields, bulkMeta, editingCols, idKey, staged, saving, cfg.readonly, canOpen, hasSoftDelete, softDeleteCol, softDeleteInvert, isDeleted, msLabelMaps, singular, handleEditOpen]);

  // `proLink` columns opt into the shared viewed-PRO pill (TanTable wraps their cells
  // in <ProTableCell>): a clicked pro goes green, with a ring on the last one opened,
  // so a user working down the list can see which pros they've already checked.
  const proLinkColumnIds = useMemo(
    () => [...fields.filter((f) => f.inTable !== false), ...extraFields]
      .filter((f) => f.proLink)
      .map((f) => f.key),
    [fields, extraFields],
  );

  // A list request is bounded by the list, not the cap, so the "most recent 10,000"
  // warning would be describing a limit that did not apply.
  const capped = serverSearch && !loading && !listFilterActive && data.length >= searchLimit;

  // What a pasted list is reconciled against on THIS grid, for the columns the server
  // cannot answer. On a capped table those columns see a page, not the table, so a miss
  // there is not evidence of anything — which the user has no way to know otherwise.
  // Columns the backend DOES answer suppress this and say they searched everything.
  const listFilterScopeNote = serverSearch
    ? `This grid holds only the most recent ${searchLimit.toLocaleString()} rows, so a `
      + `value outside that page will read as unmatched even if it exists.`
      + (cfg.listFilterColumns?.length
          ? ` ${cfg.listFilterColumns.length === 1 ? 'One column searches' : 'Some columns search'}`
            + ' the whole table instead — they say so when you paste into them.'
          : '')
    : undefined;

  const page = (
    <div className="h-full w-full p-10 pb-24">
      {cfg.latestDateFilter && (
        <label className="mb-3 flex items-center gap-2 text-sm text-label">
          {cfg.latestDateFilter.label}
          <select aria-label={cfg.latestDateFilter.label} value={dateSelection} onChange={event => setDateSelection(event.target.value)} className="rounded-md border border-gray-300 dark:border-gray-600 bg-panel px-3 py-1">
            <option value="latest">Latest{dateValues[0] ? ` (${dateValues[0]})` : ''}</option>
            <option value="">All dates</option>
            {dateValues.map(date => <option key={date} value={date}>{date}</option>)}
          </select>
        </label>
      )}
      {/* Table-level caution (cfg.notice) — shown before anything is edited, for a
          field whose effect reaches outside this system. */}
      {cfg.notice && (
        <div
          role="note"
          className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{cfg.notice}</span>
        </div>
      )}

      {capped && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Showing the most recent {searchLimit.toLocaleString()} matches. Refine your search
          {cfg.key === 'edi-events' ? ' (Pro #, EDI Handle, or Event)' : ''} to narrow the results.
        </div>
      )}
      {/* Unsaved grid edits. Deliberately loud and pinned above the table: an edit
          that looks applied but isn't is the one failure mode staging introduces, so
          the count is always on screen while anything is pending. */}
      {staged.recordCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-semibold">
            {staged.cellCount} unsaved change{staged.cellCount === 1 ? '' : 's'}
            {staged.recordCount === staged.cellCount
              ? ''
              : ` across ${staged.recordCount} record${staged.recordCount === 1 ? '' : 's'}`}
          </span>
          <span className="text-xs text-amber-800 dark:text-amber-300">
            Nothing is written until you save.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => { staged.discard(); disarmAllColumns(); }}
              disabled={saving}
              className="rounded border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-gray-900 dark:text-amber-200 dark:hover:bg-amber-900/40"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSaveEdits}
              disabled={saving}
              className="rounded border border-blue-600 bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving
                ? 'Saving…'
                : `Save ${staged.recordCount} record${staged.recordCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {deepLinkFilterChips.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-900 dark:text-blue-200">
          <span className="font-semibold">Filtered by:</span>
          {deepLinkFilterChips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white/70 px-2 py-0.5 text-xs dark:border-blue-800 dark:bg-gray-900/40"
            >
              <span className="font-medium">{c.label}:</span> {c.value}
            </span>
          ))}
          <button
            type="button"
            onClick={clearDeepLinkFilters}
            className="ml-auto rounded border border-blue-300 bg-white px-2 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:bg-gray-900 dark:text-blue-300 dark:hover:bg-blue-900/50"
          >
            View All
          </button>
        </div>
      )}
      <TanTable
        data={visibleData}
        enableColumnEditing={cfg.enableColumnEditing}
        enableTotalsBar={cfg.enableTotalsBar}
        columns={columns}
        loading={loading}
        errorMessage={loadError}
        onRetry={loadData}
        settingsKey={`${cfg.key}-table`}
        defaultGlobalFilter={initialSearch}
        searchPlaceholder={cfg.searchPlaceholder ?? `Search ${cfg.label.toLowerCase()}…`}
        cardTitle={cfg.label}
        enableGrouping={!!cfg.groupBy}
        {...(cfg.groupBy && cfg.groupBy.length > 0 ? { defaultGrouping: cfg.groupBy } : {})}
        {...(cfg.defaultSort?.length ? { defaultSorting: cfg.defaultSort.map((s) => ({ id: s.id, desc: !!s.desc })) } : {})}
        enableColumnFilters={true}
        {...(canOpen ? { lockedColumns: [{ id: 'actions', side: 'right' as const }] } : {})}
        availableFields={picker.availableFields}
        additionalFields={picker.additionalFields}
        onAdditionalFieldsChange={picker.onAdditionalFieldsChange}
        // Inline editing is armed from the column's own right-click menu. `canEdit`
        // is the same test the cell renderer uses, so a column the engine will not
        // write never offers the switch at all rather than offering a dead one.
        {...(bulkRetriggerAction.length ? { bulkActions: bulkRetriggerAction } : {})}
        canEditColumn={(colId) => !!bulkMeta[colId]}
        isColumnEditing={(colId) => editingCols.has(colId)}
        onToggleColumnEditing={toggleColumnEditing}
        {...(Object.keys(bulkMeta).length ? { onBulkEdit: handleBulkEdit } : {})}
        {...(hasSoftDelete
          ? {
              // Show/hide-deleted toggle + red rows apply whenever there's a
              // soft-delete column — including readonly tables (e.g. Salesforce's
              // IsDeleted), where there's no edit action.
              showDeletedToggle: true,
              showDeleted: includeDeleted,
              onShowDeletedChange: setIncludeDeleted,
              getRowClassName: (row: TablemaintRow, m: { zebra: boolean }) =>
                deletedRowClass(isDeleted(row), m.zebra),
            }
          : {})}
        {...(cfg.showArchivedToggle
          ? { showArchivedToggle: true, showArchived: includeArchived, onShowArchivedChange: setIncludeArchived }
          : {})}
        {...(cfg.nonBlankFilter
          ? {
              rowFilterToggle: {
                // The toggle tracks the FILTER, not the state variable: active means
                // narrowed, which is `!includeBlank`.
                active: !includeBlank,
                onChange: (active: boolean) => setIncludeBlank(!active),
                onTooltip: `Show all ${cfg.label.toLowerCase()}`,
                offTooltip: `Show only rows with ${cfg.nonBlankFilter.noun}`,
                Icon: ROW_FILTER_ICONS[cfg.nonBlankFilter.icon ?? 'alert'],
              },
            }
          : {})}
        {...(proLinkColumnIds.length > 0 ? { trackViewedColumnId: proLinkColumnIds } : {})}
        {...(serverSearch ? { onSearchChange: onServerSearch } : {})}
        {...(cfg.listFilterColumns?.length
          ? { listFilterServerColumns: cfg.listFilterColumns, onListFilterRequest: handleListFilterRequest }
          : {})}
        {...(listFilterScopeNote ? { listFilterScopeNote } : {})}
        {...(canOpen ? { onRowClick: handleEditOpen } : {})}
        {...(!cfg.readonly && !cfg.createLocked
          ? {
              onEditFilters: handleAddOpen,
              showEditFiltersButton: true,
              editFiltersButtonIcon: 'plus' as const,
              editFiltersButtonTooltip: `Add ${singular}`,
            }
          : {})}
      />

      {/* Bespoke drawer (e.g. Bill Tos, Customers) takes over the edit/create
          experience. row=null when adding a new record. */}
      {DrawerComponent && (editing || isAdding) && (
        <DrawerComponent row={editing} onClose={handleClose} onSaved={loadData} />
      )}

      {!cfg.readonly && !DrawerComponent && (
        <TablemaintDrawer
          cfg={cfg}
          isOpen={editing !== null || isAdding}
          row={editing}
          onClose={handleClose}
          onSaved={handleSaved}
          options={availableOptions}
          renderPanels={DRAWER_PANELS[cfg.key]}
        />
      )}

      {/* Pro opened from a proLink cell — standard CMS pro drawer (lazy-loaded). */}
      {selectedPro && (
        <Suspense fallback={null}>
          <ProDetailsView isOpen pronumber={selectedPro} onClose={() => setSelectedPro(null)} />
        </Suspense>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}

      {/* Mass-edit failures stay up until dismissed — they name the records that
          didn't take, which is the whole point of per-row reporting. */}
      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
          />
        </NotificationContainer>
      )}
    </div>
  );

  // Page-wide viewed-PRO state for `proLink` columns (and the pro drawer they open).
  // Wrapped rather than nested inline so the page body stays at one indent level.
  return <ViewedProsProvider>{page}</ViewedProsProvider>;
};

export default TablemaintPage;
